import { useState, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Building2, Trash2, Edit, Users, Plus, Search, FileText, Mail, Settings } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useLocation } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const UFS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];
const TEXTO_ENDERECO_VALIDO = /^[\p{L}\p{N}]+(?: [\p{L}\p{N}]+)*$/u;

export default function Emitentes() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showCobrancaModal, setShowCobrancaModal] = useState(false);
  const [selectedEmitenteCobranca, setSelectedEmitenteCobranca] = useState<any>(null);
  const [cobrancaForm, setCobrancaForm] = useState({
    resendApiKey: "",
    resendFromEmail: "",
    resendFromName: "",
    zApiInstanceId: "",
    zApiClientToken: "",
    zApiSecurityToken: "",
    cobrancaTextoAdicional: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showClientesModal, setShowClientesModal] = useState(false);
  const [selectedEmitente, setSelectedEmitente] = useState<any>(null);
  const [showIEModal, setShowIEModal] = useState(false);
  const [selectedEmitenteForIE, setSelectedEmitenteForIE] = useState<number | null>(null);
  const [newIEUf, setNewIEUf] = useState("");
  const [newIEValue, setNewIEValue] = useState("");

  // Form state
  const [cnpj, setCnpj] = useState("");
  const [razaoSocial, setRazaoSocial] = useState("");
  const [nomeFantasia, setNomeFantasia] = useState("");
  const [inscricaoEstadual, setInscricaoEstadual] = useState("");
  const [endereco, setEndereco] = useState("");
  const [logradouro, setLogradouro] = useState("");
  const [cep, setCep] = useState("");
  const [numero, setNumero] = useState("");
  const [bairro, setBairro] = useState("");
  const [municipio, setMunicipio] = useState("");
  const [uf, setUf] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");
  const [operadorLogistico, setOperadorLogistico] = useState(false);
  const [tipoRegime, setTipoRegime] = useState("");
  const [valorM2Armazenado, setValorM2Armazenado] = useState("");
  const [valorPacoteExpedido, setValorPacoteExpedido] = useState("");
  const [valorPacoteEnvioFull, setValorPacoteEnvioFull] = useState("");
  const [valorUnidadeRetrabalhada, setValorUnidadeRetrabalhada] = useState("");

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  // Queries
  const { data: emitentes = [], refetch, isLoading } = trpc.emitentes.list.useQuery();
  const { data: quoteTypesList = [] } = trpc.quoteTypes.list.useQuery();
  const { data: priceTablesList = [] } = trpc.priceTables.list.useQuery();
  const { data: municipios = [] } = trpc.municipiosIbge.byUf.useQuery({ uf }, { enabled: !!uf });
  const { data: clientes = [] } = trpc.emitentes.clientes.useQuery(
    { emitenteId: selectedEmitente?.id || 0 },
    { enabled: !!selectedEmitente }
  );

  // Mutations
  const createMutation = trpc.emitentes.create.useMutation({
    onSuccess: () => {
      toast.success("Emitente criado com sucesso!");
      resetForm();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao criar emitente");
    },
  });

  const updateMutation = trpc.emitentes.update.useMutation({
    onSuccess: () => {
      toast.success("Emitente atualizado com sucesso!");
      resetForm();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar emitente");
    },
  });

  const updateXmlConfigMutation = trpc.emitentesXmlConfig.update.useMutation({
    onSuccess: () => {
      toast.success("Configuração XML salva com sucesso!");
      setShowXmlConfigModal(false);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao salvar configuração XML");
    },
  });

  const deleteMutation = trpc.emitentes.delete.useMutation({
    onSuccess: () => {
      toast.success("Emitente deletado com sucesso!");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao deletar emitente");
    },
  });
  
  // Queries e mutations de Inscricoes Estaduais
  const { data: inscricoesEstaduais = [], refetch: refetchIE } = trpc.emitentes.inscricoesEstaduais.useQuery(
    { emitenteId: selectedEmitenteForIE || 0 },
    { enabled: !!selectedEmitenteForIE }
  );
  
  const createIEMutation = trpc.emitentes.createInscricaoEstadual.useMutation({
    onSuccess: () => {
      toast.success("Inscricao Estadual salva com sucesso!");
      refetchIE();
      setNewIEUf("");
      setNewIEValue("");
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao salvar inscricao estadual");
    },
  });
  
  const deleteIEMutation = trpc.emitentes.deleteInscricaoEstadual.useMutation({
    onSuccess: () => {
      toast.success("Inscricao Estadual removida!");
      refetchIE();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao remover inscricao estadual");
    },
  });

  const [showTesteWhatsappModal, setShowTesteWhatsappModal] = useState(false);
  const [showXmlConfigModal, setShowXmlConfigModal] = useState(false);
  const [selectedEmitenteXmlConfig, setSelectedEmitenteXmlConfig] = useState<any>(null);
  const [xmlQuoteTypeId, setXmlQuoteTypeId] = useState<string>("");
  const [xmlPriceTableId, setXmlPriceTableId] = useState<string>("");
  const [testeWhatsappNumero, setTesteWhatsappNumero] = useState("");
  const [testeWhatsappMensagem, setTesteWhatsappMensagem] = useState("");
  const testeWhatsappMutation = trpc.emitentes.testeWhatsapp.useMutation({
    onSuccess: () => {
      toast.success("Mensagem de teste enviada com sucesso!");
      setShowTesteWhatsappModal(false);
      setTesteWhatsappNumero("");
      setTesteWhatsappMensagem("");
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao enviar mensagem de teste");
    },
  });

  const resetForm = () => {
    setCnpj("");
    setRazaoSocial("");
    setNomeFantasia("");
    setInscricaoEstadual("");
    setEndereco("");
    setLogradouro("");
    setCep("");
    setNumero("");
    setBairro("");
    setMunicipio("");
    setUf("");
    setTelefone("");
    setEmail("");
    setTipoRegime("");
    setValorM2Armazenado("");
    setValorPacoteExpedido("");
    setValorPacoteEnvioFull("");
    setValorUnidadeRetrabalhada("");
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (emitente: any) => {
    setCnpj(emitente.cnpj);
    setRazaoSocial(emitente.razaoSocial);
    setNomeFantasia(emitente.nomeFantasia || "");
    setInscricaoEstadual(emitente.inscricaoEstadual || "");
    setEndereco(emitente.endereco || "");
    setLogradouro(emitente.logradouro || "");
    setCep(emitente.cep || "");
    setNumero(emitente.numero || "");
    setBairro(emitente.bairro || "");
    setMunicipio(emitente.municipio || "");
    setUf(emitente.uf || "");
    setTelefone(emitente.telefone || "");
    setEmail(emitente.email || "");
    setOperadorLogistico(!!emitente.operadorLogistico);
    setTipoRegime(emitente.tipoRegime || "");
    setValorM2Armazenado(emitente.valorM2Armazenado != null ? String(emitente.valorM2Armazenado) : "");
    setValorPacoteExpedido(emitente.valorPacoteExpedido != null ? String(emitente.valorPacoteExpedido) : "");
    setValorPacoteEnvioFull(emitente.valorPacoteEnvioFull != null ? String(emitente.valorPacoteEnvioFull) : "");
    setValorUnidadeRetrabalhada(emitente.valorUnidadeRetrabalhada != null ? String(emitente.valorUnidadeRetrabalhada) : "");
    setEditingId(emitente.id);
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!cnpj || !razaoSocial) {
      toast.error("CNPJ e Razão Social são obrigatórios");
      return;
    }

    const data = {
      cnpj: cnpj.replace(/\D/g, ""),
      razaoSocial,
      nomeFantasia: nomeFantasia || undefined,
      inscricaoEstadual: inscricaoEstadual || undefined,
      endereco: endereco || undefined,
      logradouro: logradouro || undefined,
      cep: cep || undefined,
      numero: numero || undefined,
      bairro: bairro || undefined,
      municipio: municipio || undefined,
      uf: uf || undefined,
      telefone: telefone || undefined,
      email: email || undefined,
      tipoRegime: tipoRegime || undefined,
      operadorLogistico: editingId ? operadorLogistico : undefined,
      valorM2Armazenado: valorM2Armazenado.trim() === "" ? null : Number(valorM2Armazenado.replace(",", ".")),
      valorPacoteExpedido: valorPacoteExpedido.trim() === "" ? null : Number(valorPacoteExpedido.replace(",", ".")),
      valorPacoteEnvioFull: valorPacoteEnvioFull.trim() === "" ? null : Number(valorPacoteEnvioFull.replace(",", ".")),
      valorUnidadeRetrabalhada: valorUnidadeRetrabalhada.trim() === "" ? null : Number(valorUnidadeRetrabalhada.replace(",", ".")),
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, ...data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (id: number) => {
    if (confirm("Tem certeza que deseja deletar este emitente?")) {
      deleteMutation.mutate({ id });
    }
  };

  const formatCnpj = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    return numbers
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .slice(0, 18);
  };

  // Filtrar emitentes
  const filteredEmitentes = emitentes.filter((e: any) => {
    const s = searchTerm.toLowerCase();
    return (
      e.razaoSocial.toLowerCase().includes(s) ||
      e.cnpj.toLowerCase().includes(s) ||
      (e.nomeFantasia && e.nomeFantasia.toLowerCase().includes(s))
    );
  });

  // Loading e auth checks
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (user.role !== "admin") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Acesso Negado</CardTitle>
            <CardDescription>
              Apenas administradores podem acessar esta página.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setLocation("/fila")}>Voltar para Fila</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => setLocation("/usuarios")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold">Emitentes</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar emitente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            <Button size="sm" onClick={() => setShowForm(!showForm)}>
              <Plus className="h-4 w-4 mr-2" />
              {showForm ? "Cancelar" : "Novo Emitente"}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Formulário de cadastro */}
        {showForm && (
          <Card>
            <CardHeader>
              <CardTitle>{editingId ? "Editar Emitente" : "Novo Emitente"}</CardTitle>
              <CardDescription>Preencha os dados do emitente</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">CNPJ *</label>
                    <Input
                      value={cnpj}
                      onChange={(e) => setCnpj(formatCnpj(e.target.value))}
                      placeholder="00.000.000/0000-00"
                      maxLength={18}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Razão Social *</label>
                    <Input
                      value={razaoSocial}
                      onChange={(e) => setRazaoSocial(e.target.value)}
                      placeholder="Razão Social da empresa"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Nome Fantasia</label>
                    <Input
                      value={nomeFantasia}
                      onChange={(e) => setNomeFantasia(e.target.value)}
                      placeholder="Nome Fantasia"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Inscrições Estaduais</label>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        if (editingId) {
                          setSelectedEmitenteForIE(editingId);
                          setShowIEModal(true);
                        } else {
                          toast.error("Salve o emitente primeiro para gerenciar inscrições estaduais");
                        }
                      }}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Gerenciar Inscrições Estaduais
                    </Button>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium">Logradouro</label>
                    <Input
                      value={logradouro}
                      onChange={(e) => {
                        const valor = e.target.value;
                        if (!valor || TEXTO_ENDERECO_VALIDO.test(valor)) setLogradouro(valor);
                      }}
                      placeholder="Rua, Avenida, etc."
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">CEP</label>
                    <Input
                      value={cep}
                      onChange={(e) => setCep(e.target.value)}
                      placeholder="00000-000"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Número</label>
                    <Input
                      value={numero}
                      onChange={(e) => {
                        const valor = e.target.value;
                        if (!valor || TEXTO_ENDERECO_VALIDO.test(valor)) setNumero(valor);
                      }}
                      placeholder="123"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Bairro</label>
                    <Input
                      value={bairro}
                      onChange={(e) => setBairro(e.target.value)}
                      placeholder="Bairro"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Município</label>
                    <select className="mt-1 h-10 w-full rounded-md border bg-background px-3" value={municipio} disabled={!uf} onChange={(e) => setMunicipio(e.target.value)}>
                      <option value="">{uf ? "Selecione o município" : "Selecione a UF primeiro"}</option>
                      {municipio && !(municipios as any[]).some((item: any) => item.nome === municipio) && <option value={municipio}>Valor legado: {municipio}</option>}
                      {(municipios as any[]).map((item: any) => <option key={item.id} value={item.nome}>{item.nome}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">UF</label>
                    <select className="mt-1 h-10 w-full rounded-md border bg-background px-3" value={uf} onChange={(e) => { setUf(e.target.value); setMunicipio(""); }}>
                      <option value="">Selecione a UF</option>
                      {UFS.map((sigla) => <option key={sigla} value={sigla}>{sigla}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Telefone</label>
                    <Input
                      value={telefone}
                      onChange={(e) => setTelefone(e.target.value)}
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">E-mail</label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="email@empresa.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Regime Tributário</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={tipoRegime}
                    onChange={(e) => setTipoRegime(e.target.value)}
                  >
                    <option value="">Selecione o regime tributário</option>
                    <option value="simples_nacional">Simples Nacional (CRT 1)</option>
                    <option value="simples_nacional_excesso">Simples Nacional — Excesso (CRT 2)</option>
                    <option value="regime_normal">Regime Normal (CRT 3)</option>
                    <option value="mei">MEI (CRT 4)</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Valor M² armazenado</label>
                    <Input value={valorM2Armazenado} onChange={(e) => setValorM2Armazenado(e.target.value)} inputMode="decimal" placeholder="0,0000" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Valor pacote expedido</label>
                    <Input value={valorPacoteExpedido} onChange={(e) => setValorPacoteExpedido(e.target.value)} inputMode="decimal" placeholder="0,0000" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Valor pacote envio Full</label>
                    <Input value={valorPacoteEnvioFull} onChange={(e) => setValorPacoteEnvioFull(e.target.value)} inputMode="decimal" placeholder="0,0000" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Valor unidade retrabalhada</label>
                    <Input value={valorUnidadeRetrabalhada} onChange={(e) => setValorUnidadeRetrabalhada(e.target.value)} inputMode="decimal" placeholder="0,0000" />
                  </div>
                </div>
                {editingId && (
                  <div className="flex items-center gap-3 p-3 border rounded-md bg-orange-50 border-orange-200">
                    <input
                      type="checkbox"
                      id="operadorLogistico"
                      checked={operadorLogistico}
                      onChange={(e) => setOperadorLogistico(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-orange-600"
                    />
                    <div>
                      <label htmlFor="operadorLogistico" className="text-sm font-medium cursor-pointer">
                        Operador Logístico
                      </label>
                      <p className="text-xs text-muted-foreground">Apenas um emitente pode ser o operador logístico. Ao marcar este campo, os demais serão desmarcados automaticamente.</p>
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                    {editingId ? "Atualizar" : "Cadastrar"}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Lista de emitentes */}
        <Card>
          <CardHeader>
            <CardTitle>Emitentes Cadastrados</CardTitle>
            <CardDescription>
              {filteredEmitentes.length} emitente(s) encontrado(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : filteredEmitentes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum emitente encontrado
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>CNPJ</TableHead>
                    <TableHead>Razão Social</TableHead>
                    <TableHead>Nome Fantasia</TableHead>
                    <TableHead>Telefone</TableHead>
                    <TableHead>E-mail</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmitentes.map((emitente: any) => (
                    <TableRow key={emitente.id}>
                      <TableCell className="font-mono">{formatCnpj(emitente.cnpj)}</TableCell>
                      <TableCell className="font-medium">{emitente.razaoSocial}</TableCell>
                      <TableCell>{emitente.nomeFantasia || "-"}</TableCell>
                      <TableCell>{emitente.telefone || "-"}</TableCell>
                      <TableCell>{emitente.email || "-"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedEmitente(emitente);
                              setShowClientesModal(true);
                            }}
                            title="Ver Clientes"
                          >
                            <Users className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedEmitenteCobranca(emitente);
                              setCobrancaForm({
                                resendApiKey: emitente.resendApiKey || "",
                                resendFromEmail: emitente.resendFromEmail || "",
                                resendFromName: emitente.resendFromName || "",
                                zApiInstanceId: emitente.zApiInstanceId || "",
                                zApiClientToken: emitente.zApiClientToken || "",
                                zApiSecurityToken: emitente.zApiSecurityToken || "",
                                cobrancaTextoAdicional: emitente.cobrancaTextoAdicional || "",
                              });
                              setShowCobrancaModal(true);
                            }}
                            title="Envio de cobrança"
                          >
                            <Mail className="h-4 w-4 text-blue-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedEmitenteXmlConfig(emitente);
                              setXmlQuoteTypeId(emitente.xmlQuoteTypeId ? String(emitente.xmlQuoteTypeId) : "");
                              setXmlPriceTableId(emitente.xmlPriceTableId ? String(emitente.xmlPriceTableId) : "");
                              setShowXmlConfigModal(true);
                            }}
                            title="Config. Importação XML"
                          >
                            <Settings className="h-4 w-4 text-purple-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(emitente)}
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(emitente.id)}
                            title="Deletar"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal de Inscricoes Estaduais */}
      <Dialog open={showIEModal} onOpenChange={setShowIEModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Inscricoes Estaduais</DialogTitle>
            <DialogDescription>
              Gerencie as inscricoes estaduais por UF
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="w-24">
                <Input
                  value={newIEUf}
                  onChange={(e) => setNewIEUf(e.target.value.toUpperCase())}
                  placeholder="UF"
                  maxLength={2}
                />
              </div>
              <div className="flex-1">
                <Input
                  value={newIEValue}
                  onChange={(e) => setNewIEValue(e.target.value)}
                  placeholder="Inscricao Estadual"
                />
              </div>
              <Button
                onClick={() => {
                  if (!newIEUf || !newIEValue) {
                    toast.error("Preencha UF e Inscricao Estadual");
                    return;
                  }
                  createIEMutation.mutate({
                    emitenteId: selectedEmitenteForIE!,
                    uf: newIEUf,
                    inscricaoEstadual: newIEValue,
                  });
                }}
                disabled={createIEMutation.isPending}
              >
                <Plus className="h-4 w-4 mr-1" />
                Adicionar
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>UF</TableHead>
                  <TableHead>Inscricao Estadual</TableHead>
                  <TableHead className="w-20">Acoes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inscricoesEstaduais.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground">
                      Nenhuma inscricao estadual cadastrada
                    </TableCell>
                  </TableRow>
                ) : (
                  inscricoesEstaduais.map((ie: any) => (
                    <TableRow key={ie.id}>
                      <TableCell className="font-medium">{ie.uf}</TableCell>
                      <TableCell>{ie.inscricaoEstadual}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteIEMutation.mutate({ id: ie.id })}
                          disabled={deleteIEMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Envio de Cobrança */}
      <Dialog open={showCobrancaModal} onOpenChange={setShowCobrancaModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Envio de Cobrança</DialogTitle>
            <DialogDescription>
              Configure os canais de envio de cobrança para {selectedEmitenteCobranca?.nomeFantasia || selectedEmitenteCobranca?.razaoSocial}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {/* Seção E-mail (Resend) */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-semibold">E-mail via Resend</h3>
              </div>
              <div className="grid grid-cols-1 gap-3 pl-6">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">API Key do Resend</label>
                  <Input
                    type="password"
                    value={cobrancaForm.resendApiKey}
                    onChange={(e) => setCobrancaForm(f => ({ ...f, resendApiKey: e.target.value }))}
                    placeholder="re_xxxxxxxxxxxx"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">E-mail Remetente</label>
                    <Input
                      type="email"
                      value={cobrancaForm.resendFromEmail}
                      onChange={(e) => setCobrancaForm(f => ({ ...f, resendFromEmail: e.target.value }))}
                      placeholder="cobranca@empresa.com"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Nome Remetente</label>
                    <Input
                      value={cobrancaForm.resendFromName}
                      onChange={(e) => setCobrancaForm(f => ({ ...f, resendFromName: e.target.value }))}
                      placeholder="Empresa Ltda"
                    />
                  </div>
                </div>
              </div>
            </div>

            <hr />

            {/* Seção WhatsApp (Z-API) */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm">💬</span>
                <h3 className="text-sm font-semibold">WhatsApp via Z-API</h3>
              </div>
              <div className="grid grid-cols-1 gap-3 pl-6">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Instance ID</label>
                    <Input
                      value={cobrancaForm.zApiInstanceId}
                      onChange={(e) => setCobrancaForm(f => ({ ...f, zApiInstanceId: e.target.value }))}
                      placeholder="3A0B1C2D3E4F"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Token da Instância</label>
                    <Input
                      type="password"
                      value={cobrancaForm.zApiClientToken}
                      onChange={(e) => setCobrancaForm(f => ({ ...f, zApiClientToken: e.target.value }))}
                      placeholder="token..."
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Token de Segurança da Conta</label>
                    <Input
                      type="password"
                      value={cobrancaForm.zApiSecurityToken}
                      onChange={(e) => setCobrancaForm(f => ({ ...f, zApiSecurityToken: e.target.value }))}
                      placeholder="token..."
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Obtenha as credenciais em{" "}
                  <a href="https://app.z-api.io" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                    app.z-api.io
                  </a>
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setShowTesteWhatsappModal(true)}
                  disabled={!cobrancaForm.zApiInstanceId || !cobrancaForm.zApiClientToken || !cobrancaForm.zApiSecurityToken}
                >
                  Teste WhatsApp
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Texto adicional da cobrança</label>
              <textarea
                className="w-full min-h-[100px] resize-y rounded-md border p-2 text-sm"
                value={cobrancaForm.cobrancaTextoAdicional}
                onChange={(e) => setCobrancaForm(f => ({ ...f, cobrancaTextoAdicional: e.target.value }))}
                placeholder="Opcional. Será incluído somente na cobrança do pedido deste emitente."
                maxLength={4000}
              />
              <p className="text-xs text-muted-foreground">Não é usado no lembrete automático de vencimento.</p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCobrancaModal(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (!selectedEmitenteCobranca) return;
                  updateMutation.mutate({
                    id: selectedEmitenteCobranca.id,
                    resendApiKey: cobrancaForm.resendApiKey || undefined,
                    resendFromEmail: cobrancaForm.resendFromEmail || undefined,
                    resendFromName: cobrancaForm.resendFromName || undefined,
                    zApiInstanceId: cobrancaForm.zApiInstanceId || undefined,
                    zApiClientToken: cobrancaForm.zApiClientToken || undefined,
                    zApiSecurityToken: cobrancaForm.zApiSecurityToken || undefined,
                    cobrancaTextoAdicional: cobrancaForm.cobrancaTextoAdicional,
                  });
                  setShowCobrancaModal(false);
                }}
                disabled={updateMutation.isPending}
              >
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Teste WhatsApp */}
      <Dialog open={showTesteWhatsappModal} onOpenChange={setShowTesteWhatsappModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Teste WhatsApp</DialogTitle>
            <DialogDescription>
              Envie uma mensagem de teste via Z-API
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Número</label>
              <Input
                value={testeWhatsappNumero}
                onChange={(e) => setTesteWhatsappNumero(e.target.value)}
                placeholder="11999999999"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Mensagem</label>
              <textarea
                className="w-full border rounded-md p-2 text-sm min-h-[100px] resize-y"
                value={testeWhatsappMensagem}
                onChange={(e) => setTesteWhatsappMensagem(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowTesteWhatsappModal(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (!selectedEmitenteCobranca) return;
                  testeWhatsappMutation.mutate({
                    emitenteId: selectedEmitenteCobranca.id,
                    numero: testeWhatsappNumero,
                    mensagem: testeWhatsappMensagem,
                  });
                }}
                disabled={testeWhatsappMutation.isPending || !testeWhatsappNumero || !testeWhatsappMensagem}
              >
                {testeWhatsappMutation.isPending ? "Enviando..." : "Enviar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Clientes */}
      <Dialog open={showClientesModal} onOpenChange={setShowClientesModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Clientes de {selectedEmitente?.razaoSocial}</DialogTitle>
            <DialogDescription>
              Usuários vinculados a este emitente
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {clientes.length > 0 ? (
              clientes.map((cliente: any) => (
                <div
                  key={cliente.id}
                  className="flex items-center justify-between p-3 bg-muted rounded-lg"
                >
                  <div>
                    <p className="font-medium">{cliente.name}</p>
                    <p className="text-sm text-muted-foreground">{cliente.email}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                Nenhum cliente vinculado a este emitente
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Modal Config XML */}
      <Dialog open={showXmlConfigModal} onOpenChange={setShowXmlConfigModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configuração Importação XML</DialogTitle>
            <DialogDescription>
              Defina o tipo de orçamento e tabela de preço padrão para importação XML de {selectedEmitenteXmlConfig?.razaoSocial}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Tipo de Orçamento Padrão</label>
              <Select value={xmlQuoteTypeId} onValueChange={setXmlQuoteTypeId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {(quoteTypesList || []).map((qt: any) => (
                    <SelectItem key={qt.id} value={String(qt.id)}>
                      {qt.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Tabela de Preço Padrão</label>
              <Select value={xmlPriceTableId} onValueChange={setXmlPriceTableId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {(priceTablesList || []).map((pt: any) => (
                    <SelectItem key={pt.id} value={String(pt.id)}>
                      {pt.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowXmlConfigModal(false)}>Cancelar</Button>
              <Button onClick={() => {
                if (!selectedEmitenteXmlConfig) return;
                updateXmlConfigMutation.mutate({
                  id: selectedEmitenteXmlConfig.id,
                  xmlQuoteTypeId: xmlQuoteTypeId ? Number(xmlQuoteTypeId) : null,
                  xmlPriceTableId: xmlPriceTableId ? Number(xmlPriceTableId) : null,
                });
              }}>Salvar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
